import { TOPICS, type Lang, type Page, type Topic } from './content';

export { TOPICS, type Lang, type Page, type Topic };
export { CHAPTERS, GLOSSARY, INTRODUCTION, ROLES, type Chapter } from './guide';

/** Topics that apply to someone, given what they may do. */
export function topicsFor(can: (permission: string) => boolean): Topic[] {
  return TOPICS.filter((t) => t.requires === null || can(t.requires));
}

const fold = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[’']/g, ' ');
const STOP = new Set(
  'le la les un une des du de d l et ou a au aux en dans sur pour par avec mon ma mes je j est ce cette comment faire peut puis on qui que quoi quel quelle the a an of to in on for with my i how do can is what where'.split(' '),
);
const words = (s: string) => fold(s).split(/[^a-z0-9]+/).filter((w) => w.length > 2 && !STOP.has(w));

/** Simple keyword search over the topics (both languages): title words count triple; prefixes match (réserv → réservation). */
export function searchTopics(query: string, topics: Topic[] = TOPICS, limit = 3): Topic[] {
  const q = [...new Set(words(query))];
  if (q.length === 0) return [];
  const scored = topics.map((t) => {
    const title = words(`${t.title.fr} ${t.title.en}`);
    const body = words(`${t.body.fr.join(' ')} ${t.body.en.join(' ')}`);
    const hit = (list: string[], w: string) => list.some((x) => x.startsWith(w) || (x.length > 4 && w.startsWith(x)));
    const score = q.reduce((n, w) => n + (hit(title, w) ? 3 : 0) + (hit(body, w) ? 1 : 0), 0);
    return { t, score };
  });
  return scored
    .filter((x) => x.score >= 2)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.t);
}

/** A topic as plain text, for the questions agent. */
export const topicText = (t: Topic, lang: Lang) => `${t.title[lang]}\n${t.body[lang].map((p, i) => `${i + 1}. ${p}`).join('\n')}`;

// --- Owners' associations (OBNL with servitudes): the guide is written for syndicates of co-owners; for an association
// its vocabulary changes (members, lots, letters patent and by-laws, reserve fund) and Bill 16 does not apply.

export type Structure = 'divided_coownership' | 'npo_with_servitudes';

const ASSOCIATION: Record<Lang, [RegExp, string][]> = {
  fr: [
    [/Les quotes-parts \(valeurs relatives de la déclaration\)/g, 'Les parts fixées par les règlements'],
    [/la déclaration de copropriété, les règlements/g, 'les lettres patentes, les règlements'],
    [/la déclaration ou les règlements/g, 'les règlements'],
    [/tous vos syndicats/g, 'toutes vos associations'],
    [/unités concernées/g, 'lots concernés'],
    [/valeurs relatives de la déclaration/g, 'parts fixées par les règlements'],
    [/fixée par la déclaration de copropriété/g, 'fixée par les règlements de l’association'],
    [/déclaration de copropriété/g, 'règlements de l’association'],
    [/Déclaration, règlements/g, 'Lettres patentes, règlements'],
    [/déclaration, règlements/g, 'lettres patentes, règlements'],
    [/la déclaration/g, 'les règlements'],
    [/ \(Loi 16\)/g, ''],
    [/fonds de prévoyance/g, 'fonds de réserve'],
    [/Fonds de prévoyance/g, 'Fonds de réserve'],
    [/copropriétaires/g, 'membres'],
    [/Copropriétaires/g, 'Membres'],
    [/copropriétaire/g, 'membre'],
    [/Copropriétaire/g, 'Membre'],
    [/des syndicats/g, 'des associations'],
    [/du syndicat/g, 'de l’association'],
    [/au syndicat/g, 'à l’association'],
    [/le syndicat/g, 'l’association'],
    [/Le syndicat/g, 'L’association'],
    [/votre syndicat/g, 'votre association'],
    [/chaque syndicat/g, 'chaque association'],
    [/un syndicat/g, 'une association'],
    [/ce syndicat/g, 'cette association'],
    [/syndicats/g, 'associations'],
    [/syndicat/g, 'association'],
    [/quotes-parts/g, 'parts'],
    [/Quotes-parts/g, 'Parts'],
    [/quote-part/g, 'part'],
    [/Quote-part/g, 'Part'],
    [/une unité/g, 'un lot'],
    [/cette unité/g, 'ce lot'],
    [/de l’unité/g, 'du lot'],
    [/à l’unité/g, 'au lot'],
    [/l’unité/g, 'le lot'],
    [/unités possédées/g, 'lots possédés'],
    [/unités/g, 'lots'],
    [/Unités/g, 'Lots'],
    [/unité/g, 'lot'],
    [/Unité/g, 'Lot'],
  ],
  en: [
    [/relative values from the declaration/g, 'set by the by-laws'],
    [/set by the declaration of co-ownership/g, "set by the association's by-laws"],
    [/declaration of co-ownership/g, "association's by-laws"],
    [/Declaration, by-laws/g, 'Letters patent, by-laws'],
    [/declaration, by-laws/g, 'letters patent, by-laws'],
    [/the declaration/g, 'the by-laws'],
    [/ \(Bill 16\)/g, ''],
    [/contingency fund/g, 'reserve fund'],
    [/Contingency fund/g, 'Reserve fund'],
    [/co-owners/g, 'members'],
    [/Co-owners/g, 'Members'],
    [/co-owner/g, 'member'],
    [/Co-owner/g, 'Member'],
    [/syndicates of co-owners/g, 'associations'],
    [/syndicates/g, 'associations'],
    [/syndicate/g, 'association'],
    [/Syndicate/g, 'Association'],
    [/quote-parts/g, 'shares'],
    [/Quote-parts/g, 'Shares'],
    [/quote-part/g, 'share'],
    [/Quote-part/g, 'Share'],
    [/\bunits\b/g, 'lots'],
    [/\bUnits\b/g, 'Lots'],
    [/\bunit\b/g, 'lot'],
    [/\bUnit\b/g, 'Lot'],
  ],
};

/** A text of the guide in the client's vocabulary. Sentences that already speak of both kinds of clients are kept. */
export function adaptText(text: string, lang: Lang, structure: Structure): string {
  if (structure !== 'npo_with_servitudes' || /association/i.test(text)) return text;
  return ASSOCIATION[lang].reduce((s, [pattern, replacement]) => s.replace(pattern, replacement), text);
}

const adaptRecord = (r: Record<Lang, string>, structure: Structure): Record<Lang, string> => ({ fr: adaptText(r.fr, 'fr', structure), en: adaptText(r.en, 'en', structure) });
const adaptList = (r: Record<Lang, string[]>, structure: Structure): Record<Lang, string[]> => ({
  fr: r.fr.map((p) => adaptText(p, 'fr', structure)),
  en: r.en.map((p) => adaptText(p, 'en', structure)),
});

/** Topics in the client's vocabulary. */
export function topicsForStructure(topics: Topic[], structure: Structure): Topic[] {
  if (structure !== 'npo_with_servitudes') return topics;
  return topics.map((t) => ({ ...t, title: adaptRecord(t.title, structure), body: adaptList(t.body, structure) }));
}

export { adaptList, adaptRecord };
